import React, { useMemo, useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { ArrowLeft, Download, Loader2, Droplets, Zap } from "lucide-react";
import Topbar from "Components/Topbar/Topbar";
import logoSrc from "../../Assets/logo_new.jpeg";
import { useGetEauKpis, useGetEnergieKpis } from "../../hooks/analytics/useGetKpisAnalytics";
import { useGetEauAnalytics } from "../../hooks/analytics/useGetModuleAnalytics";
import { useGetEauRatios } from "../../hooks/analytics/useGetEauRatios";
import { useGetRawEnergiePerTypeBU, type RawEnergieTypeResult } from "../../hooks/analytics/useGetRawEnergiePerTypeBU";
import { useGetPosteChampsMetier } from "../../hooks/metadata/useGetPosteChampsMetier";
import { useGetPosteDimensions } from "../../hooks/metadata/useGetPosteDimensions";
import { useGetPostesEmission } from "../../hooks/emission/useGetPostesEmission";
import { useGetBUs } from "../../hooks/reference/useGetBUs";
import type { KpiTimeSeriesEntry } from "../../hooks/analytics/useGetKpisAnalytics";
import type { User as BackendUser } from "../../types/user";
import type { ReportModule, ReportConfig } from "./ReportsPage";
import { useGetReportAiSummary } from "../../hooks/report/useGetReportAiSummary";
import "./ReportPreviewPage.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend, ChartDataLabels);

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_LABELS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const MONTH_FULL = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const FAMILLE_COLORS: Record<string, string> = {
  AGRUMES: "#f59e0b", AFOURER: "#f59e0b", CLEMENTINE: "#ecc55f", ORANGE: "#cf9168",
  "AUTRES AGRUMES": "#E8EC53", "AUTRES FRUITIERS": "#699511", "AUTRES ARBO": "#699511",
  AVOCAT: "#067800", OLIVIER: "#2C4D13", CEREALES: "#7a7b53",
  "FRUITS ROUGES": "#8924ad", MARAICHAGE: "#abc340", "PLANTES AROMATIQUES": "#086e00",
};

const CHART_FALLBACK_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#14b8a6", "#f97316", "#06b6d4", "#84cc16", "#6366f1",
];

const ENERGY_TYPE_COLORS: Record<string, string> = {
  ENERGIE_ELEC_TEP: "#3b82f6",
  ENERGIE_GASOIL_TEP: "#16a34a",
  ENERGIE_ESSENCE_TEP: "#8b5cf6",
  ENERGIE_FUEL_TEP: "#ef4444",
  ENERGIE_AUTRES_TEP: "#f59e0b",
};

const RAW_ENERGY_DISPLAY: Array<{ code: string; label: string; unit: string; color: string }> = [
  { code: "ELEC", label: "Électricité", unit: "KWh", color: "#3b82f6" },
  { code: "GASOIL", label: "Gasoil", unit: "L", color: "#16a34a" },
  { code: "FUEL", label: "Fuel", unit: "Kg", color: "#ef4444" },
  { code: "ESSENCE", label: "Essence", unit: "L", color: "#8b5cf6" },
];

const TEP_TO_GWH = 11630 / 1_000_000;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatLarge(num: number): string {
  const abs = Math.abs(num);
  let s: string;
  if (abs >= 1_000_000) s = `${(abs / 1_000_000).toFixed(1)}M`;
  else if (abs >= 1_000) s = `${(abs / 1_000).toFixed(1)}k`;
  else s = abs.toFixed(1);
  return num < 0 ? `-${s}` : s;
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function getColor(name: string, map: Record<string, string>, idx: number): string {
  const upper = name.toUpperCase().trim();
  if (map[upper]) return map[upper];
  for (const [k, c] of Object.entries(map)) {
    if (upper.includes(k) || k.includes(upper)) return c;
  }
  return CHART_FALLBACK_COLORS[idx % CHART_FALLBACK_COLORS.length];
}

function buildMonthRange(startMonth: number, endMonth: number) {
  return Array.from({ length: endMonth - startMonth + 1 }, (_, i) => startMonth + i);
}

function extractMonthTotal(ts: KpiTimeSeriesEntry[], year: number, month: number, codes: string[]): number {
  const entry = ts.find((t) => {
    const [y, m] = t.month.split("-").map(Number);
    return y === year && m === month;
  });
  if (!entry) return 0;
  return codes.reduce((s, c) => s + ((entry[c] as number) || 0), 0);
}

function extractAnalyticsMonth(ts: Array<{ month: string; valeur: number }>, year: number, month: number): number {
  const entry = ts.find((t) => {
    const [y, m] = t.month.split("-").map(Number);
    return y === year && m === month;
  });
  return entry?.valeur ?? 0;
}

function findEvoRatio(evo: Array<{ month: string; ratio: number }>, year: number, month: number): number {
  const entry = evo.find((e) => {
    const [y, m] = e.month.split("-").map(Number);
    return y === year && m === month;
  });
  return entry?.ratio ?? 0;
}

function sumGroupsByMonth(
  groups: Array<{ timeSeries: Array<{ month: string; valeur: number }> }>,
  year: number,
  month: number
): number {
  return groups.reduce((sum, g) => {
    const entry = g.timeSeries.find((t) => {
      const [y, m] = t.month.split("-").map(Number);
      return y === year && m === month;
    }, 0);
    return sum + ((entry as any)?.valeur ?? 0);
  }, 0);
}

function buildComparisonBarData(
  nTs: KpiTimeSeriesEntry[],
  n1Ts: KpiTimeSeriesEntry[],
  codes: string[],
  startMonth: number,
  endMonth: number,
  yearN: number,
  yearN1: number,
  colorN: string
) {
  const months = buildMonthRange(startMonth, endMonth);
  return {
    labels: months.map((m) => MONTH_LABELS[m - 1]),
    datasets: [
      {
        label: campaignLabel(yearN),
        data: months.map((m) => extractMonthTotal(nTs, yearN, m, codes)),
        backgroundColor: colorN + "CC",
        borderColor: colorN,
        borderWidth: 1,
        borderRadius: 5,
        borderSkipped: false as const,
      },
      {
        label: campaignLabel(yearN1),
        data: months.map((m) => extractMonthTotal(n1Ts, yearN1, m, codes)),
        backgroundColor: "#94a3b8CC",
        borderColor: "#94a3b8",
        borderWidth: 1,
        borderRadius: 5,
        borderSkipped: false as const,
      },
    ],
  };
}

function buildPie(labels: string[], data: number[], colors: string[]) {
  return {
    labels,
    datasets: [{ data, backgroundColor: colors, borderWidth: 2, borderColor: "#fff" }],
  };
}

function campaignLabel(year: number) {
  return `${String(year).slice(-2)}/${String(year + 1).slice(-2)}`;
}

function avgRatio(evo: Array<{ ratio: number }>): number {
  if (!evo.length) return 0;
  return evo.reduce((s, e) => s + e.ratio, 0) / evo.length;
}

function findTypeData(dataByType: Record<string, RawEnergieTypeResult>, code: string): RawEnergieTypeResult | undefined {
  const upper = code.toUpperCase();
  const entry = Object.entries(dataByType).find(([k]) => {
    const c = k.toUpperCase();
    return c === upper || c.includes(upper) || upper.includes(c);
  });
  return entry?.[1];
}

// ─── Chart options ─────────────────────────────────────────────────────────────

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "52%",
  plugins: {
    legend: {
      position: "right" as const,
      labels: { padding: 10, usePointStyle: true, pointStyle: "circle" as const, font: { size: 10 }, color: "#475569" },
    },
    tooltip: {
      callbacks: {
        label: (ctx: any) => {
          const total = (ctx.dataset.data as number[]).reduce((a: number, b: number) => a + b, 0);
          return `${formatLarge(ctx.parsed)} | ${total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0}%`;
        },
      },
    },
    datalabels: {
      display: (ctx: any) => {
        const data = ctx.dataset.data as number[];
        const total = data.reduce((a: number, b: number) => a + b, 0);
        return total > 0 && (data[ctx.dataIndex] ?? 0) / total >= 0.04;
      },
      formatter: (value: number, ctx: any) => {
        const data = ctx.dataset.data as number[];
        const total = data.reduce((a: number, b: number) => a + b, 0);
        if (total === 0) return "";
        return `${((value / total) * 100).toFixed(0)}%\n${formatLarge(value)}`;
      },
      color: "#fff",
      font: { size: 10, weight: "bold" as const },
      textAlign: "center" as const,
    },
  },
};

function barOptions(unit: string) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 22, left: 4, right: 4, bottom: 4 } },
    plugins: {
      legend: { position: "top" as const, labels: { usePointStyle: true, font: { size: 10 }, color: "#475569" } },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${formatLarge(ctx.parsed.y)} ${unit}` } },
      datalabels: {
        display: true,
        anchor: "end" as const,
        align: "end" as const,
        offset: 2,
        formatter: (v: number) => v > 0 ? formatLarge(v) : "",
        font: { size: 9, weight: "bold" as const },
        color: "#374151",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: "#f1f5f9" },
        ticks: { color: "#94a3b8", font: { size: 10 }, callback: (v: any) => formatLarge(Number(v)) },
      },
      x: { grid: { display: false }, ticks: { color: "#475569", font: { size: 10 } } },
    },
  };
}

function consolideeBarOptions(unit: string) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 26, left: 4, right: 4, bottom: 4 } },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx: any) => `${formatLarge(ctx.parsed.y)} ${unit}` } },
      datalabels: {
        display: true,
        anchor: "end" as const,
        align: "end" as const,
        offset: 2,
        formatter: (v: number) => v > 0 ? `${formatLarge(v)} ${unit}` : "",
        font: { weight: "bold" as const, size: 11 },
        color: "#374151",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: "#f1f5f9" },
        ticks: { color: "#94a3b8", font: { size: 10 }, callback: (v: any) => formatLarge(Number(v)) },
      },
      x: { grid: { display: false }, ticks: { color: "#475569", font: { size: 11 } } },
    },
  };
}

function hBarOptions(unit: string) {
  return {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 70, top: 4, bottom: 4 } },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.parsed.x.toFixed(1)} ${unit}` } },
      datalabels: {
        display: true,
        anchor: "end" as const,
        align: "end" as const,
        offset: 4,
        formatter: (v: number) => v > 0 ? `${v.toFixed(1)} ${unit}` : "",
        font: { size: 10, weight: "bold" as const },
        color: "#374151",
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: "#f1f5f9" },
        ticks: { color: "#94a3b8", font: { size: 10 } },
      },
      y: { grid: { display: false }, ticks: { color: "#475569", font: { size: 10 } } },
    },
  };
}

// ─── Water Report Content ──────────────────────────────────────────────────────

const WaterReportContent: React.FC<{ config: ReportConfig }> = ({ config }) => {
  const { yearN, yearN1, startMonth, endMonth, filters } = config;

  const fromN = `${yearN}-${pad(startMonth)}`;
  const toN = `${yearN}-${pad(endMonth)}`;
  const fromN1 = `${yearN1}-${pad(startMonth)}`;
  const toN1 = `${yearN1}-${pad(endMonth)}`;

  const fp = useMemo(() => ({
    ...(filters.bus.length ? { bu_ids: filters.bus.map(Number) } : {}),
    ...(filters.activites.length ? { activite_ids: filters.activites.map(Number) } : {}),
    ...(filters.filieres.length ? { filiere_ids: filters.filieres.map(Number) } : {}),
    ...(filters.regions.length ? { region_ids: filters.regions.map(Number) } : {}),
    ...(filters.sites.length ? { site_ids: filters.sites.map(Number) } : {}),
  }), [filters]);

  // ── KPI data (famille dimension) ──────────────────────────────────────────
  const { data: eauN } = useGetEauKpis({ from_period: fromN, to_period: toN, dimension_type: "famille", ...fp });
  const { data: eauN1 } = useGetEauKpis({ from_period: fromN1, to_period: toN1, dimension_type: "famille", ...fp });

  const totalN = useMemo(() => eauN?.summary?.reduce((s, i) => s + i.total, 0) ?? 0, [eauN]);
  const totalN1 = useMemo(() => eauN1?.summary?.reduce((s, i) => s + i.total, 0) ?? 0, [eauN1]);

  // ── Water sources ─────────────────────────────────────────────────────────
  const { data: champsMetier = [] } = useGetPosteChampsMetier({ posteId: 2 });
  const champSurfaceId = useMemo(() => champsMetier.find((c) => c.code === "EAU_Surface_m3")?.id ?? null, [champsMetier]);
  const champSouterraineId = useMemo(() => champsMetier.find((c) => c.code === "EAU_Souterraine_m3")?.id ?? null, [champsMetier]);
  const champReseauId = useMemo(() => champsMetier.find((c) => c.code === "EAU_Réseau_m3")?.id ?? null, [champsMetier]);
  const champTraiteeId = useMemo(() => champsMetier.find((c) => c.code === "EAU_Traitée_m3")?.id ?? null, [champsMetier]);

  const baseEau = { dimension_type: "famille" as const, ...fp };
  const { data: surfN } = useGetEauAnalytics({ from_period: fromN, to_period: toN, champ_metier: champSurfaceId, ...baseEau });
  const { data: soutN } = useGetEauAnalytics({ from_period: fromN, to_period: toN, champ_metier: champSouterraineId, ...baseEau });
  const { data: resN } = useGetEauAnalytics({ from_period: fromN, to_period: toN, champ_metier: champReseauId, ...baseEau });
  const { data: surfN1 } = useGetEauAnalytics({ from_period: fromN1, to_period: toN1, champ_metier: champSurfaceId, ...baseEau });
  const { data: soutN1 } = useGetEauAnalytics({ from_period: fromN1, to_period: toN1, champ_metier: champSouterraineId, ...baseEau });
  const { data: resN1 } = useGetEauAnalytics({ from_period: fromN1, to_period: toN1, champ_metier: champReseauId, ...baseEau });

  // ── Rejets traités ────────────────────────────────────────────────────────
  const { data: traiteeN } = useGetEauAnalytics({ from_period: fromN, to_period: toN, champ_metier: champTraiteeId, ...baseEau });
  const { data: traiteeN1 } = useGetEauAnalytics({ from_period: fromN1, to_period: toN1, champ_metier: champTraiteeId, ...baseEau });

  // ── Ratios (m³/ha) ────────────────────────────────────────────────────────
  const ratiosN = useGetEauRatios({ from_period: fromN, to_period: toN, ...fp });
  const ratiosN1 = useGetEauRatios({ from_period: fromN1, to_period: toN1, ...fp });

  // ── Derived values ────────────────────────────────────────────────────────

  const consoParHaN = useMemo(() => avgRatio(ratiosN.evoConsoEau), [ratiosN.evoConsoEau]);
  const consoParHaN1 = useMemo(() => avgRatio(ratiosN1.evoConsoEau), [ratiosN1.evoConsoEau]);

  const tauxTraitement = useMemo(() => {
    const rejets = traiteeN?.summary?.total ?? 0;
    if (totalN === 0 || rejets === 0) return null;
    return (rejets / totalN) * 100;
  }, [traiteeN, totalN]);

  // ── Pie charts ────────────────────────────────────────────────────────────

  const famillePieN = useMemo(() => {
    if (!eauN?.summary?.length) return null;
    const labels = eauN.summary.map((i) => i.name);
    const data = eauN.summary.map((i) => i.total);
    return buildPie(labels, data, labels.map((l, idx) => getColor(l, FAMILLE_COLORS, idx)));
  }, [eauN]);

  const sourcesPieN = useMemo(() => {
    const d = [surfN?.summary?.total ?? 0, soutN?.summary?.total ?? 0, resN?.summary?.total ?? 0];
    if (!d.some((v) => v > 0)) return null;
    return buildPie(["Eau Surface", "Eau Souterraine", "Eau Réseau"], d, ["#3b82f6", "#8b5cf6", "#10b981"]);
  }, [surfN, soutN, resN]);

  // ── Bar charts ─────────────────────────────────────────────────────────────

  const evolutionBar = useMemo(() => {
    const codes = (eauN?.summary ?? []).map((i) => i.code);
    if (!codes.length) return null;
    return buildComparisonBarData(eauN?.time_series ?? [], eauN1?.time_series ?? [], codes, startMonth, endMonth, yearN, yearN1, "#3b82f6");
  }, [eauN, eauN1, startMonth, endMonth, yearN, yearN1]);

  const consolideeBar = useMemo(() => {
    if (totalN === 0 && totalN1 === 0) return null;
    return {
      labels: [campaignLabel(yearN), campaignLabel(yearN1)],
      datasets: [{ data: [totalN, totalN1], backgroundColor: ["#3b82f6CC", "#94a3b8CC"], borderColor: ["#3b82f6", "#94a3b8"], borderWidth: 1, borderRadius: 5, borderSkipped: false as const }],
    };
  }, [totalN, totalN1, yearN, yearN1]);

  const evoRatioBar = useMemo(() => {
    const months = buildMonthRange(startMonth, endMonth);
    const nData = months.map((m) => findEvoRatio(ratiosN.evoConsoEau, yearN, m));
    const n1Data = months.map((m) => findEvoRatio(ratiosN1.evoConsoEau, yearN1, m));
    if (!nData.some((v) => v > 0) && !n1Data.some((v) => v > 0)) return null;
    return {
      labels: months.map((m) => MONTH_LABELS[m - 1]),
      datasets: [
        { label: campaignLabel(yearN), data: nData, backgroundColor: "#3b82f6CC", borderColor: "#3b82f6", borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
        { label: campaignLabel(yearN1), data: n1Data, backgroundColor: "#94a3b8CC", borderColor: "#94a3b8", borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
      ],
    };
  }, [ratiosN.evoConsoEau, ratiosN1.evoConsoEau, startMonth, endMonth, yearN, yearN1]);

  const ratioConsolideeBar = useMemo(() => {
    if (consoParHaN === 0 && consoParHaN1 === 0) return null;
    return {
      labels: [campaignLabel(yearN), campaignLabel(yearN1)],
      datasets: [{ data: [consoParHaN, consoParHaN1], backgroundColor: ["#3b82f6CC", "#94a3b8CC"], borderColor: ["#3b82f6", "#94a3b8"], borderWidth: 1, borderRadius: 5, borderSkipped: false as const }],
    };
  }, [consoParHaN, consoParHaN1, yearN, yearN1]);

  const ratioFamilleBar = useMemo(() => {
    if (!ratiosN.ratioFamille.length) return null;
    return {
      labels: ratiosN.ratioFamille.map((r) => r.name),
      datasets: [{
        label: "m³/ha",
        data: ratiosN.ratioFamille.map((r) => Math.round(r.ratio)),
        backgroundColor: ratiosN.ratioFamille.map((r, i) => getColor(r.name, FAMILLE_COLORS, i) + "CC"),
        borderRadius: 4,
        borderSkipped: false as const,
      }],
    };
  }, [ratiosN.ratioFamille]);

  const rejetsEvolutionBar = useMemo(() => {
    const months = buildMonthRange(startMonth, endMonth);
    const nData = months.map((m) => extractAnalyticsMonth(traiteeN?.time_series ?? [], yearN, m));
    const n1Data = months.map((m) => extractAnalyticsMonth(traiteeN1?.time_series ?? [], yearN1, m));
    if (!nData.some((v) => v > 0) && !n1Data.some((v) => v > 0)) return null;
    return {
      labels: months.map((m) => MONTH_LABELS[m - 1]),
      datasets: [
        { label: campaignLabel(yearN), data: nData, backgroundColor: "#8b5cf6CC", borderColor: "#8b5cf6", borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
        { label: campaignLabel(yearN1), data: n1Data, backgroundColor: "#94a3b8CC", borderColor: "#94a3b8", borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
      ],
    };
  }, [traiteeN, traiteeN1, startMonth, endMonth, yearN, yearN1]);

  const rejetsConsolideeBar = useMemo(() => {
    const tN = traiteeN?.summary?.total ?? 0;
    const tN1 = traiteeN1?.summary?.total ?? 0;
    if (tN === 0 && tN1 === 0) return null;
    return {
      labels: [campaignLabel(yearN), campaignLabel(yearN1)],
      datasets: [{ data: [tN, tN1], backgroundColor: ["#8b5cf6CC", "#94a3b8CC"], borderColor: ["#8b5cf6", "#94a3b8"], borderWidth: 1, borderRadius: 5, borderSkipped: false as const }],
    };
  }, [traiteeN, traiteeN1, yearN, yearN1]);

  const ratioFamilleHeight = Math.max(200, (ratiosN.ratioFamille.length || 6) * 34);

  // ── Sources pie (N vs N-1) ────────────────────────────────────────────────
  const sourcesPieN1 = useMemo(() => {
    const d = [surfN1?.summary?.total ?? 0, soutN1?.summary?.total ?? 0, resN1?.summary?.total ?? 0];
    if (!d.some((v) => v > 0)) return null;
    return buildPie(["Eau Surface", "Eau Souterraine", "Eau Réseau"], d, ["#3b82f6", "#8b5cf6", "#10b981"]);
  }, [surfN1, soutN1, resN1]);

  return (
    <>
      {/* ── 1) Situation Globales ── */}
      <div className="rp-section">
        <h3 className="rp-section-number">1) Situation Globales :</h3>

        <div className="rp-simple-kpi-row">
          <div className="rp-simple-kpi-card">
            <div className="rp-simple-kpi-label">Total</div>
            <div className="rp-simple-kpi-value" style={{ color: "#3b82f6" }}>
              {formatLarge(totalN)} <span className="rp-simple-kpi-unit">m³</span>
            </div>
          </div>
          <div className="rp-simple-kpi-card" style={{ borderColor: consoParHaN > 0 ? "#3b82f6" : "#e2e8f0" }}>
            <div className="rp-simple-kpi-label">Consommation par ha</div>
            {consoParHaN > 0 ? (
              <div className="rp-simple-kpi-value" style={{ color: "#3b82f6" }}>
                {consoParHaN.toFixed(1)} <span className="rp-simple-kpi-unit">m³/ha</span>
              </div>
            ) : (
              <div className="rp-simple-kpi-placeholder">— m³/ha</div>
            )}
          </div>
          <div className="rp-simple-kpi-card" style={{ borderColor: tauxTraitement != null ? "#8b5cf6" : "#e2e8f0" }}>
            <div className="rp-simple-kpi-label">Taux de traitement</div>
            {tauxTraitement != null ? (
              <div className="rp-simple-kpi-value" style={{ color: "#8b5cf6" }}>
                {tauxTraitement.toFixed(1)} <span className="rp-simple-kpi-unit">%</span>
              </div>
            ) : (
              <div className="rp-simple-kpi-placeholder">— %</div>
            )}
          </div>
        </div>

        <div className="rp-chart-row">
          <div className="rp-chart-col">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#10b981" }}>Famille Split (m³)</div>
              <div className="rp-pie-canvas">
                {famillePieN
                  ? <Doughnut data={famillePieN} options={pieOptions} />
                  : <div className="rp-no-data">Aucune donnée</div>}
              </div>
            </div>
          </div>
          <div className="rp-chart-col">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#10b981" }}>Ratio Famille (m³/ha)</div>
              {ratioFamilleBar ? (
                <div style={{ height: ratioFamilleHeight, position: "relative" }}>
                  <Bar data={ratioFamilleBar} options={hBarOptions("m³/ha")} />
                </div>
              ) : (
                <div className="rp-placeholder-body" style={{ height: 200 }}>
                  <span className="rp-placeholder-text">Données en cours de chargement…</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Sources d'eau ── */}
      <div className="rp-section">
        <div className="rp-chart-row">
          <div className="rp-chart-col">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#3b82f6" }}>Répartition par source ({campaignLabel(yearN)})</div>
              <div className="rp-pie-canvas">
                {sourcesPieN ? <Doughnut data={sourcesPieN} options={pieOptions} /> : <div className="rp-no-data">Aucune donnée</div>}
              </div>
            </div>
          </div>
          <div className="rp-chart-col">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#94a3b8" }}>Répartition par source ({campaignLabel(yearN1)})</div>
              <div className="rp-pie-canvas">
                {sourcesPieN1 ? <Doughnut data={sourcesPieN1} options={pieOptions} /> : <div className="rp-no-data">Aucune donnée</div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 2) Evolution des consommations ── */}
      <div className="rp-section">
        <h3 className="rp-section-number">2) Evolution des consommations :</h3>

        <div className="rp-chart-row">
          <div className="rp-chart-col rp-chart-col-wide">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#3b82f6" }}>Evolution de la consommation (m³)</div>
              <div className="rp-bar-canvas-sm">
                {evolutionBar ? <Bar data={evolutionBar} options={barOptions("m³")} /> : <div className="rp-no-data">Aucune donnée</div>}
              </div>
            </div>
          </div>
          <div className="rp-chart-col rp-chart-col-narrow">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#3b82f6" }}>Evolution consolidée (m³)</div>
              <div className="rp-bar-canvas-sm">
                {consolideeBar ? <Bar data={consolideeBar} options={consolideeBarOptions("m³")} /> : <div className="rp-no-data">Aucune donnée</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="rp-chart-row rp-chart-row-mt">
          <div className="rp-chart-col rp-chart-col-wide">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#3b82f6" }}>Evolution de la consommation (m³/ha)</div>
              <div className="rp-bar-canvas-sm">
                {evoRatioBar ? <Bar data={evoRatioBar} options={barOptions("m³/ha")} /> : <div className="rp-no-data">Données en cours de chargement…</div>}
              </div>
            </div>
          </div>
          <div className="rp-chart-col rp-chart-col-narrow">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#3b82f6" }}>Evolution consolidée (m³/ha)</div>
              <div className="rp-bar-canvas-sm">
                {ratioConsolideeBar ? <Bar data={ratioConsolideeBar} options={consolideeBarOptions("m³/ha")} /> : <div className="rp-no-data">Données en cours de chargement…</div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 3) Rejets liquides ── */}
      <div className="rp-section">
        <h3 className="rp-section-number">3) Rejets liquides :</h3>

        <div className="rp-chart-row">
          <div className="rp-chart-col rp-chart-col-wide">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#8b5cf6" }}>Evolution des Rejets liquides traités (m³)</div>
              <div className="rp-bar-canvas-sm">
                {rejetsEvolutionBar ? <Bar data={rejetsEvolutionBar} options={barOptions("m³")} /> : <div className="rp-no-data">Données en cours de chargement…</div>}
              </div>
            </div>
          </div>
          <div className="rp-chart-col rp-chart-col-narrow">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#8b5cf6" }}>Evolution consolidée (m³)</div>
              <div className="rp-bar-canvas-sm">
                {rejetsConsolideeBar ? <Bar data={rejetsConsolideeBar} options={consolideeBarOptions("m³")} /> : <div className="rp-no-data">Données en cours de chargement…</div>}
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

// ─── Energy Report Content ────────────────────────────────────────────────────

const EnergyReportContent: React.FC<{ config: ReportConfig }> = ({ config }) => {
  const { yearN, yearN1, startMonth, endMonth, filters } = config;

  const fromN = `${yearN}-${pad(startMonth)}`;
  const toN = `${yearN}-${pad(endMonth)}`;
  const fromN1 = `${yearN1}-${pad(startMonth)}`;
  const toN1 = `${yearN1}-${pad(endMonth)}`;

  const fp = useMemo(() => ({
    ...(filters.bus.length ? { bu_ids: filters.bus.map(Number) } : {}),
    ...(filters.activites.length ? { activite_ids: filters.activites.map(Number) } : {}),
    ...(filters.filieres.length ? { filiere_ids: filters.filieres.map(Number) } : {}),
    ...(filters.regions.length ? { region_ids: filters.regions.map(Number) } : {}),
    ...(filters.sites.length ? { site_ids: filters.sites.map(Number) } : {}),
  }), [filters]);

  // ── TEP KPIs ──────────────────────────────────────────────────────────────
  const { data: engN } = useGetEnergieKpis({ from_period: fromN, to_period: toN, ...fp });
  const { data: engN1 } = useGetEnergieKpis({ from_period: fromN1, to_period: toN1, ...fp });

  const totalN = useMemo(() => engN?.summary?.reduce((s, i) => s + i.total, 0) ?? 0, [engN]);
  const totalN1 = useMemo(() => engN1?.summary?.reduce((s, i) => s + i.total, 0) ?? 0, [engN1]);

  // ── Metadata for raw energy per type ─────────────────────────────────────
  const { data: postesEmission = [] } = useGetPostesEmission();
  const energiePoste = useMemo(
    () => postesEmission.find((p) => p.name.toLowerCase().includes("energie")),
    [postesEmission]
  );

  const { data: energyChampsMetier = [] } = useGetPosteChampsMetier({
    posteId: energiePoste?.id || 0,
    enabled: !!energiePoste?.id,
  });
  const champUniteId = useMemo(
    () => energyChampsMetier.find((c) => c.code === "TOTAL_Unité")?.id ?? null,
    [energyChampsMetier]
  );

  const { data: energieDimensions = [] } = useGetPosteDimensions({
    posteId: energiePoste?.id || 0,
    enabled: !!energiePoste?.id,
  });
  const energieTypeDimension = useMemo(
    () => energieDimensions.find((d) => d.code === "energie_type") ?? null,
    [energieDimensions]
  );

  const { data: bus = [] } = useGetBUs();
  const buNamesMap = useMemo(() => {
    const map: Record<number, string> = {};
    bus.forEach((b: any) => { map[b.id] = b.name ?? b.nom ?? String(b.id); });
    return map;
  }, [bus]);

  const perBUIds = useMemo(() => {
    if (filters.bus.length > 0) return filters.bus.map(Number);
    return bus.map((b: any) => b.id);
  }, [filters.bus, bus]);

  const rawBaseN = useMemo(() => ({ from_period: fromN, to_period: toN, ...fp }), [fromN, toN, fp]);
  const rawBaseN1 = useMemo(() => ({ from_period: fromN1, to_period: toN1, ...fp }), [fromN1, toN1, fp]);

  const { dataByType: rawByTypeN } = useGetRawEnergiePerTypeBU({
    baseFilters: rawBaseN,
    champMetierId: champUniteId,
    energieDimension: energieTypeDimension,
    perBUIds,
    buNamesMap,
    enabled: !!champUniteId && !!energieTypeDimension && perBUIds.length > 0,
  });

  const { dataByType: rawByTypeN1 } = useGetRawEnergiePerTypeBU({
    baseFilters: rawBaseN1,
    champMetierId: champUniteId,
    energieDimension: energieTypeDimension,
    perBUIds,
    buNamesMap,
    enabled: !!champUniteId && !!energieTypeDimension && perBUIds.length > 0,
  });

  // ── Pie chart ─────────────────────────────────────────────────────────────
  const typePieN = useMemo(() => {
    if (!engN?.summary?.length) return null;
    const labels = engN.summary.map((i) => i.name);
    const data = engN.summary.map((i) => i.total);
    const colors = engN.summary.map((i) => ENERGY_TYPE_COLORS[i.code] ?? "#94a3b8");
    return buildPie(labels, data, colors);
  }, [engN]);

  // ── Per-type KPI cards (TEP) ──────────────────────────────────────────────
  const typeKpis = useMemo(() => [
    { code: "ENERGIE_ELEC_TEP", name: "Électricité", unit: "TEP", color: ENERGY_TYPE_COLORS.ENERGIE_ELEC_TEP },
    { code: "ENERGIE_GASOIL_TEP", name: "Gasoil", unit: "TEP", color: ENERGY_TYPE_COLORS.ENERGIE_GASOIL_TEP },
    { code: "ENERGIE_FUEL_TEP", name: "Fuel", unit: "TEP", color: ENERGY_TYPE_COLORS.ENERGIE_FUEL_TEP },
    { code: "ENERGIE_ESSENCE_TEP", name: "Essence", unit: "TEP", color: ENERGY_TYPE_COLORS.ENERGIE_ESSENCE_TEP },
    { code: "ENERGIE_AUTRES_TEP", name: "Autres", unit: "TEP", color: ENERGY_TYPE_COLORS.ENERGIE_AUTRES_TEP },
  ].map((t) => ({ ...t, value: engN?.summary?.find((i) => i.code === t.code)?.total ?? 0 })), [engN]);

  // ── Global TEP evolution bar ──────────────────────────────────────────────
  const evolutionBar = useMemo(() => {
    const codes = (engN?.summary ?? []).map((i) => i.code);
    if (!codes.length) return null;
    return buildComparisonBarData(engN?.time_series ?? [], engN1?.time_series ?? [], codes, startMonth, endMonth, yearN, yearN1, "#f59e0b");
  }, [engN, engN1, startMonth, endMonth, yearN, yearN1]);

  const consolideeBar = useMemo(() => {
    if (totalN === 0 && totalN1 === 0) return null;
    return {
      labels: [campaignLabel(yearN), campaignLabel(yearN1)],
      datasets: [{ data: [totalN, totalN1], backgroundColor: ["#f59e0bCC", "#94a3b8CC"], borderColor: ["#f59e0b", "#94a3b8"], borderWidth: 1, borderRadius: 5, borderSkipped: false as const }],
    };
  }, [totalN, totalN1, yearN, yearN1]);

  // ── Per-type native unit evolution bars ──────────────────────────────────
  const perTypeCharts = useMemo(() => {
    return RAW_ENERGY_DISPLAY.map((t) => {
      const typeN = findTypeData(rawByTypeN, t.code);
      const typeN1 = findTypeData(rawByTypeN1, t.code);
      const groupsN = typeN?.groups ?? [];
      const groupsN1 = typeN1?.groups ?? [];
      const months = buildMonthRange(startMonth, endMonth);

      const nData = months.map((m) => sumGroupsByMonth(groupsN, yearN, m));
      const n1Data = months.map((m) => sumGroupsByMonth(groupsN1, yearN1, m));
      const hasEvo = nData.some((v) => v > 0) || n1Data.some((v) => v > 0);

      const totalTypeN = groupsN.reduce((s, g) => s + (g as any).total, 0);
      const totalTypeN1 = groupsN1.reduce((s, g) => s + (g as any).total, 0);
      const hasCons = totalTypeN > 0 || totalTypeN1 > 0;

      return {
        ...t,
        evolutionBar: hasEvo ? {
          labels: months.map((m) => MONTH_LABELS[m - 1]),
          datasets: [
            { label: campaignLabel(yearN), data: nData, backgroundColor: t.color + "CC", borderColor: t.color, borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
            { label: campaignLabel(yearN1), data: n1Data, backgroundColor: "#94a3b8CC", borderColor: "#94a3b8", borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
          ],
        } : null,
        consolideeBar: hasCons ? {
          labels: [campaignLabel(yearN), campaignLabel(yearN1)],
          datasets: [{ data: [totalTypeN, totalTypeN1], backgroundColor: [t.color + "CC", "#94a3b8CC"], borderColor: [t.color, "#94a3b8"], borderWidth: 1, borderRadius: 5, borderSkipped: false as const }],
        } : null,
      };
    });
  }, [rawByTypeN, rawByTypeN1, startMonth, endMonth, yearN, yearN1]);

  const rawReady = !!champUniteId && !!energieTypeDimension && perBUIds.length > 0;

  // ── Section 3: EnR charts ─────────────────────────────────────────────────
  const elecGWhBar = useMemo(() => {
    const tsN = engN?.time_series ?? [];
    const tsN1 = engN1?.time_series ?? [];
    const months = buildMonthRange(startMonth, endMonth);
    const nData = months.map((m) => extractMonthTotal(tsN, yearN, m, ["ENERGIE_ELEC_TEP"]) * TEP_TO_GWH);
    const n1Data = months.map((m) => extractMonthTotal(tsN1, yearN1, m, ["ENERGIE_ELEC_TEP"]) * TEP_TO_GWH);
    if (!nData.some((v) => v > 0) && !n1Data.some((v) => v > 0)) return null;
    return {
      labels: months.map((m) => MONTH_LABELS[m - 1]),
      datasets: [
        { label: campaignLabel(yearN), data: nData, backgroundColor: "#3b82f6CC", borderColor: "#3b82f6", borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
        { label: campaignLabel(yearN1), data: n1Data, backgroundColor: "#94a3b8CC", borderColor: "#94a3b8", borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
      ],
    };
  }, [engN, engN1, startMonth, endMonth, yearN, yearN1]);

  // Helper: sum raw solar kWh across all BUs for a given year+month
  const getSolarKWh = (dataByType: Record<string, RawEnergieTypeResult>, year: number, month: number): number => {
    const solarType = findTypeData(dataByType, "SOLAIRE");
    if (!solarType?.groups?.length) return 0;
    const target = `${year}-${pad(month)}`;
    return solarType.groups.reduce((sum, g) => {
      const entry = g.timeSeries.find((t) => t.month === target);
      return sum + (entry?.valeur ?? 0);
    }, 0);
  };

  const pvMWhBar = useMemo(() => {
    const months = buildMonthRange(startMonth, endMonth);
    const KWH_TO_MWH = 1 / 1_000;
    const nData = months.map((m) => getSolarKWh(rawByTypeN, yearN, m) * KWH_TO_MWH);
    const n1Data = months.map((m) => getSolarKWh(rawByTypeN1, yearN1, m) * KWH_TO_MWH);
    if (!nData.some((v) => v > 0) && !n1Data.some((v) => v > 0)) return null;
    return {
      labels: months.map((m) => MONTH_LABELS[m - 1]),
      datasets: [
        { label: campaignLabel(yearN), data: nData, backgroundColor: "#22c55eCC", borderColor: "#22c55e", borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
        { label: campaignLabel(yearN1), data: n1Data, backgroundColor: "#94a3b8CC", borderColor: "#94a3b8", borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
      ],
    };
  }, [rawByTypeN, rawByTypeN1, startMonth, endMonth, yearN, yearN1]);

  const tauxAutoBar = useMemo(() => {
    const tsN = engN?.time_series ?? [];
    const tsN1 = engN1?.time_series ?? [];
    const months = buildMonthRange(startMonth, endMonth);
    // elec is in TEP → kWh = TEP × 11630; solar is already in kWh
    const TEP_TO_KWH = 11630;
    const nData = months.map((m) => {
      const elecKWh = extractMonthTotal(tsN, yearN, m, ["ENERGIE_ELEC_TEP"]) * TEP_TO_KWH;
      const solarKWh = getSolarKWh(rawByTypeN, yearN, m);
      return elecKWh > 0 ? (solarKWh / elecKWh) * 100 : 0;
    });
    const n1Data = months.map((m) => {
      const elecKWh = extractMonthTotal(tsN1, yearN1, m, ["ENERGIE_ELEC_TEP"]) * TEP_TO_KWH;
      const solarKWh = getSolarKWh(rawByTypeN1, yearN1, m);
      return elecKWh > 0 ? (solarKWh / elecKWh) * 100 : 0;
    });
    if (!nData.some((v) => v > 0) && !n1Data.some((v) => v > 0)) return null;
    return {
      labels: months.map((m) => MONTH_LABELS[m - 1]),
      datasets: [
        { label: campaignLabel(yearN), data: nData, backgroundColor: "#f59e0bCC", borderColor: "#f59e0b", borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
        { label: campaignLabel(yearN1), data: n1Data, backgroundColor: "#94a3b8CC", borderColor: "#94a3b8", borderWidth: 1, borderRadius: 5, borderSkipped: false as const },
      ],
    };
  }, [engN, engN1, rawByTypeN, rawByTypeN1, startMonth, endMonth, yearN, yearN1]);

  return (
    <>
      {/* ── 1) Situation Globale ── */}
      <div className="rp-section">
        <h3 className="rp-section-number">1) Situation Globale :</h3>

        <div className="rp-kpi-grid">
          {typeKpis.map((t) => (
            <div key={t.code} className="rp-simple-kpi-card">
              <div className="rp-simple-kpi-label">{t.name}</div>
              <div className="rp-simple-kpi-value" style={{ color: t.color }}>
                {formatLarge(t.value)} <span className="rp-simple-kpi-unit">{t.unit}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="rp-chart-block" style={{ marginTop: 20 }}>
          <div className="rp-chart-title" style={{ borderLeftColor: "#f59e0b" }}>Consommation (TEP) par Type</div>
          <div className="rp-pie-canvas">
            {typePieN ? <Doughnut data={typePieN} options={pieOptions} /> : <div className="rp-no-data">Aucune donnée</div>}
          </div>
        </div>
      </div>

      {/* ── 2) Evolution des consommations ── */}
      <div className="rp-section">
        <h3 className="rp-section-number">2) Evolution des consommations :</h3>

        {/* TEP global */}
        <div className="rp-chart-row">
          <div className="rp-chart-col rp-chart-col-wide">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#f59e0b" }}>Evolution de la consommation Energétique (TEP)</div>
              <div className="rp-bar-canvas-sm">
                {evolutionBar ? <Bar data={evolutionBar} options={barOptions("TEP")} /> : <div className="rp-no-data">Aucune donnée</div>}
              </div>
            </div>
          </div>
          <div className="rp-chart-col rp-chart-col-narrow">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#f59e0b" }}>Evolution consolidée (TEP)</div>
              <div className="rp-bar-canvas-sm">
                {consolideeBar ? <Bar data={consolideeBar} options={consolideeBarOptions("TEP")} /> : <div className="rp-no-data">Aucune donnée</div>}
              </div>
            </div>
          </div>
        </div>

        {/* Per-type native unit charts */}
        {perTypeCharts.map((t) => (
          <div key={t.code} className="rp-chart-row rp-chart-row-mt">
            <div className="rp-chart-col rp-chart-col-wide">
              <div className="rp-chart-block">
                <div className="rp-chart-title" style={{ borderLeftColor: t.color }}>
                  Evolution de la consommation {t.label} ({t.unit})
                </div>
                <div className="rp-bar-canvas-sm">
                  {t.evolutionBar
                    ? <Bar data={t.evolutionBar} options={barOptions(t.unit)} />
                    : <div className="rp-no-data">{rawReady ? "Aucune donnée" : "Chargement…"}</div>}
                </div>
              </div>
            </div>
            <div className="rp-chart-col rp-chart-col-narrow">
              <div className="rp-chart-block">
                <div className="rp-chart-title" style={{ borderLeftColor: t.color }}>
                  Evolution consolidée ({t.unit})
                </div>
                <div className="rp-bar-canvas-sm">
                  {t.consolideeBar
                    ? <Bar data={t.consolideeBar} options={consolideeBarOptions(t.unit)} />
                    : <div className="rp-no-data">{rawReady ? "Aucune donnée" : "Chargement…"}</div>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── 3) Efficacité Energétique et EnR ── */}
      <div className="rp-section">
        <h3 className="rp-section-number">3) Efficacité Energétique et EnR :</h3>
        <div className="rp-chart-row">
          <div className="rp-chart-col">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#3b82f6" }}>Evolution de la consommation électricité (GWh)</div>
              <div className="rp-bar-canvas-sm">
                {elecGWhBar ? <Bar data={elecGWhBar} options={barOptions("GWh")} /> : <div className="rp-no-data">Aucune donnée</div>}
              </div>
            </div>
          </div>
          <div className="rp-chart-col">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#22c55e" }}>Evolution de la production PV (MWh)</div>
              <div className="rp-bar-canvas-sm">
                {pvMWhBar ? <Bar data={pvMWhBar} options={barOptions("MWh")} /> : <div className="rp-no-data">Aucune donnée</div>}
              </div>
            </div>
          </div>
        </div>
        <div className="rp-chart-row rp-chart-row-mt">
          <div className="rp-chart-col">
            <div className="rp-chart-block">
              <div className="rp-chart-title" style={{ borderLeftColor: "#f59e0b" }}>Evolution du taux d'auto-consommation (%)</div>
              <div className="rp-bar-canvas-sm">
                {tauxAutoBar ? <Bar data={tauxAutoBar} options={barOptions("%")} /> : <div className="rp-no-data">Aucune donnée</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Date helpers ──────────────────────────────────────────────────────────────

function formatDateFr(date: Date): string {
  const day = date.getDate();
  const month = MONTH_FULL[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

// ─── Main Preview Page ─────────────────────────────────────────────────────────

interface ReportPreviewPageProps {
  user: BackendUser;
  module: ReportModule;
  config: ReportConfig;
  onBack: () => void;
}

const MODULE_COLORS: Record<ReportModule, { accent: string; light: string; name: string }> = {
  eau: { accent: "#3b82f6", light: "#eff6ff", name: "Rapport Eau" },
  energie: { accent: "#f59e0b", light: "#fffbeb", name: "Rapport Énergie" },
  carbone: { accent: "#059669", light: "#ecfdf5", name: "Bilan Carbone" },
};

function formatSummaryToHtml(text: string): string {
  const parts = text.trim().split(/\.\s+/);
  const sentences = parts
    .map((s, i) => {
      const t = s.trim().replace(/^[-–]\s*/, '');
      if (!t) return '';
      return i < parts.length - 1 ? t + '.' : (t.endsWith('.') ? t : t + '.');
    })
    .filter(Boolean);
  if (sentences.length === 0) return '';
  return '<ul>' + sentences.map(s => `<li>${s}</li>`).join('') + '</ul>';
}

const ReportPreviewPage: React.FC<ReportPreviewPageProps> = ({ user, module, config, onBack }) => {
  const navigate = useNavigate();
  const { yearN, yearN1, startMonth, endMonth, filters } = config;
  const meta = MODULE_COLORS[module];
  const creationDate = formatDateFr(new Date());
  const activeFilterCount = Object.values(filters).reduce((acc, arr) => acc + arr.length, 0);
  const [downloading, setDownloading] = useState(false);
  const execBodyRef = useRef<HTMLDivElement>(null);

  const aiSummaryParams = useMemo(() => ({
    report_type: module as "eau" | "energie" | "carbone",
    campaigns: [`${yearN1}/${yearN1 + 1}`, `${yearN}/${yearN + 1}`],
    start_month: startMonth,
    end_month: endMonth,
    ...(filters.bus.length ? { bu_ids: filters.bus.map(Number) } : {}),
    ...(filters.activites.length ? { activite_ids: filters.activites.map(Number) } : {}),
    ...(filters.filieres.length ? { filiere_ids: filters.filieres.map(Number) } : {}),
    ...(filters.regions.length ? { region_ids: filters.regions.map(Number) } : {}),
    ...(filters.sites.length ? { site_ids: filters.sites.map(Number) } : {}),
  }), [module, yearN, yearN1, startMonth, endMonth, filters]);

  const { data: aiSummary, isLoading: summaryLoading } = useGetReportAiSummary(aiSummaryParams);

  useEffect(() => {
    if (aiSummary?.summary && execBodyRef.current) {
      execBodyRef.current.innerHTML = formatSummaryToHtml(aiSummary.summary);
    }
  }, [aiSummary]);

  const filterLabel = activeFilterCount > 0
    ? `${activeFilterCount} filtre${activeFilterCount > 1 ? "s" : ""} appliqué${activeFilterCount > 1 ? "s" : ""}`
    : "Aucun filtre appliqué";

  const handleDownloadPdf = async () => {
    const el = document.getElementById("rp-document");
    if (!el) return;
    setDownloading(true);
    try {
      const { toPng } = await import("html-to-image");
      const { jsPDF } = await import("jspdf");
      const PIXEL_RATIO = 2;
      const FOOTER_MM = 12;

      // Hide the in-document footer — we redraw it on every PDF page instead
      const inlineFooter = el.querySelector(".rp-footer") as HTMLElement | null;
      if (inlineFooter) inlineFooter.style.display = "none";

      const elRect = el.getBoundingClientRect();

      const sectionEls = Array.from(el.querySelectorAll(".rp-cover, .rp-exec-section, .rp-section"));
      const sectionEndsCssPx = sectionEls.map((s) => s.getBoundingClientRect().bottom - elRect.top);

      const chartEls = Array.from(el.querySelectorAll(".rp-chart-row, .rp-chart-block, .rp-kpi-grid, .rp-simple-kpi-row"));
      const chartStartsCssPx = chartEls.map((s) => s.getBoundingClientRect().top - elRect.top);

      const dataUrl = await toPng(el, { cacheBust: true, backgroundColor: "#ffffff", pixelRatio: PIXEL_RATIO });

      if (inlineFooter) inlineFooter.style.display = "";

      const img = new Image();
      img.src = dataUrl;
      await new Promise<void>((res) => { img.onload = () => res(); });

      const srcCanvas = document.createElement("canvas");
      srcCanvas.width = img.naturalWidth;
      srcCanvas.height = img.naturalHeight;
      srcCanvas.getContext("2d")!.drawImage(img, 0, 0);

      // Pre-load logo for drawing in PDF footer
      const logoImg = new Image();
      logoImg.src = logoSrc;
      await new Promise<void>((res) => { logoImg.onload = () => res(); });

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const contentH = pageH - FOOTER_MM;
      const mmPerImgPx = pageW / img.naturalWidth;
      const cssPxToMm = PIXEL_RATIO * mmPerImgPx;
      const totalMm = img.naturalHeight * mmPerImgPx;
      const sectionEndsMm = sectionEndsCssPx.map((px) => px * cssPxToMm);
      const chartStartsMm = chartStartsCssPx.map((px) => px * cssPxToMm);

      const pages: { startMm: number; endMm: number }[] = [];
      let start = 0;
      while (start < totalMm - 0.5) {
        const currentStart = start;
        const naturalEnd = start + contentH;
        if (naturalEnd >= totalMm) { pages.push({ startMm: start, endMm: totalMm }); break; }

        const fittingSections = sectionEndsMm.filter((b) => b > currentStart + 5 && b <= naturalEnd);
        if (fittingSections.length > 0) {
          const end = Math.max(...fittingSections);
          pages.push({ startMm: start, endMm: end });
          start = end;
          continue;
        }

        const chartBreaks = chartStartsMm.filter((b) => b > currentStart + 10 && b < naturalEnd);
        if (chartBreaks.length > 0) {
          const end = Math.max(...chartBreaks);
          pages.push({ startMm: start, endMm: end });
          start = end;
          continue;
        }

        pages.push({ startMm: start, endMm: naturalEnd });
        start = naturalEnd;
      }

      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = img.naturalWidth;
      const sliceCtx = sliceCanvas.getContext("2d")!;

      for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage();
        const { startMm, endMm } = pages[i];
        const startPx = Math.floor(startMm / mmPerImgPx);
        const endPx = Math.ceil(endMm / mmPerImgPx);
        const heightPx = endPx - startPx;
        const heightMm = endMm - startMm;
        sliceCanvas.height = heightPx;
        sliceCtx.clearRect(0, 0, sliceCanvas.width, heightPx);
        sliceCtx.drawImage(srcCanvas, 0, startPx, img.naturalWidth, heightPx, 0, 0, img.naturalWidth, heightPx);
        pdf.addImage(sliceCanvas.toDataURL("image/png"), "PNG", 0, 0, pageW, heightMm);

        // Draw Ymeera footer pinned to the very bottom of every page
        const footerY = pageH - FOOTER_MM;
        pdf.setFillColor(248, 250, 252);
        pdf.rect(0, footerY, pageW, FOOTER_MM, "F");
        pdf.setDrawColor(226, 232, 240);
        pdf.setLineWidth(0.3);
        pdf.line(0, footerY, pageW, footerY);
        pdf.addImage(logoImg.src, "JPEG", 5, footerY + 2.5, 7, 7);
        pdf.setFontSize(8);
        pdf.setTextColor(71, 85, 105);
        pdf.text(
          `Ymeera — ${meta.name} — ${campaignLabel(yearN1)} / ${campaignLabel(yearN)}`,
          15, footerY + 7
        );
        pdf.text(creationDate, pageW - 5, footerY + 7, { align: "right" });
      }

      pdf.save(`rapport_${module}_${yearN}_vs_${yearN1}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rp-page">
      <div className="rp-topbar no-print">
        <Topbar title={meta.name} userName={user.username} onLogout={() => navigate("/")} user={user} />
      </div>

      <div className="rp-actions no-print">
        <button className="rp-btn-back" onClick={onBack}>
          <ArrowLeft size={16} /> Modifier les paramètres
        </button>
        <div className="rp-actions-right">
          <span className="rp-actions-hint">Éditez les textes directement dans l'aperçu</span>
          <button className="rp-btn-print" onClick={handleDownloadPdf} disabled={downloading} style={{ background: meta.accent }}>
            {downloading ? <Loader2 size={16} className="rp-spin" /> : <Download size={16} />}
            {downloading ? "Génération…" : "Télécharger PDF"}
          </button>
        </div>
      </div>

      <div className="rp-document-wrapper">
        <div className="rp-document" id="rp-document">

          {/* Cover */}
          <div className="rp-cover" style={{ background: meta.light, borderTopColor: meta.accent }}>
            <div className="rp-cover-top">
              <div className="rp-cover-icon" style={{ background: meta.accent }}>
                {module === "eau" ? <Droplets size={28} color="white" /> : <Zap size={28} color="white" />}
              </div>
              <div className="rp-cover-titles">
                <h1 className="rp-cover-title" style={{ color: meta.accent }}>{meta.name}</h1>
                <p className="rp-cover-subtitle">Rapport comparatif — {campaignLabel(yearN1)} vs {campaignLabel(yearN)}</p>
              </div>
            </div>
            <div className="rp-cover-meta">
              <div className="rp-cover-meta-row">
                <span className="rp-cover-meta-label">Date de création :</span>
                <span className="rp-cover-meta-value">{creationDate}</span>
              </div>
            </div>
            <div className="rp-cover-desc">
              <div className="rp-cover-desc-title">Description :</div>
              <div className="rp-cover-desc-item"><span className="rp-check" style={{ color: meta.accent }}>✓</span><span>Période : {MONTH_FULL[startMonth - 1]} – {MONTH_FULL[endMonth - 1]} ({campaignLabel(yearN)} vs {campaignLabel(yearN1)})</span></div>
              <div className="rp-cover-desc-item"><span className="rp-check" style={{ color: meta.accent }}>✓</span><span>Filtres appliqués : {filterLabel}</span></div>
              <div className="rp-cover-desc-item"><span className="rp-check" style={{ color: meta.accent }}>✓</span><span>Comparaison : {campaignLabel(yearN1)} (N-1) → {campaignLabel(yearN)} (N)</span></div>
            </div>
          </div>

          {/* Résumé exécutif */}
          <div className="rp-exec-section">
            <h2 className="rp-exec-title" style={{ color: meta.accent }}>Résumé Exécutif :</h2>
            {summaryLoading ? (
              <div className="rp-exec-loading">
                <Loader2 size={16} className="rp-spin" style={{ marginRight: 8 }} />
                Génération du résumé par l'IA en cours…
              </div>
            ) : (
              <div
                ref={execBodyRef}
                className="rp-exec-body"
                contentEditable
                suppressContentEditableWarning
              >
                {!aiSummary?.summary
                  ? `Ce rapport synthétise les performances ${module === "eau" ? "hydriques" : "énergétiques"} sur la période ${MONTH_FULL[startMonth - 1]} – ${MONTH_FULL[endMonth - 1]}, en comparaison des campagnes ${campaignLabel(yearN1)} et ${campaignLabel(yearN)}. Les résultats permettent d'identifier les tendances clés et d'orienter les actions d'optimisation.`
                  : null}
              </div>
            )}
          </div>

          {/* Module content */}
          {module === "eau" && <WaterReportContent config={config} />}
          {module === "energie" && <EnergyReportContent config={config} />}

          {/* Footer */}
          <div className="rp-footer">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <img src={logoSrc} alt="Ymeera" style={{ height: 24, width: 24, objectFit: "contain", borderRadius: 4 }} />
              <span>Ymeera — {meta.name} — {campaignLabel(yearN1)} / {campaignLabel(yearN)}</span>
            </div>
            <span>{creationDate}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPreviewPage;
